(ns ui.sticker-dialogs.model
  "The pure part of the Replicant sticker dialogs of editor2: the state
   of the open dialog lives under `[:ui/modal :sticker]` of the store,
   these functions validate and transform it."
  (:require [clojure.string :as str]
            [storrito.instagram.story.post.util :as post-util]
            [storrito.util.link :as link]))

(def hashtag-limit
  "How many hashtag stickers Instagram allows per story."
  5)

(defn remove-hash-char
  [hashtag]
  (if (str/starts-with? hashtag "#")
    (subs hashtag 1)
    hashtag))

(defn normalize-hashtag
  [hashtag]
  (some-> hashtag
          (remove-hash-char)
          (str/trim)
          (str/lower-case)))

(defn normalized-hashtags
  "The valid, distinct hashtags of `hashtags` without the hash char."
  [hashtags]
  (->> hashtags
       (map normalize-hashtag)
       (filter post-util/valid-hashtag?)
       (distinct)
       (vec)))

(defn extract-hashtags
  "The hashtags of a comma separated text, e.g. of a paste."
  [text]
  (normalized-hashtags
    (str/split (str text ",")
               #"\,+\s*")))

(defn hashtag-total
  "How many hashtag stickers the story would have with `hashtags`
   added to the `selected` ones."
  [{:keys [current-count hashtags]} pending]
  (+ (or current-count 0)
     (count (normalized-hashtags (concat hashtags pending)))))

(defn within-hashtag-limit?
  [state pending]
  (<= (hashtag-total state pending)
      hashtag-limit))

(defn pending-hashtags
  [{:keys [input]}]
  (extract-hashtags input))

(defn hashtags-for-submit
  [{:keys [hashtags] :as state}]
  (normalized-hashtags (concat hashtags
                               (pending-hashtags state))))

(defn add-hashtags
  [state hashtags]
  (assoc state
         :hashtags (normalized-hashtags (concat (:hashtags state)
                                                hashtags))
         :input ""
         :error nil))

(defn commit-input
  "Enter, the Add button or a paste: moves the typed hashtags into the
   selection, unless the limit would be exceeded."
  [state]
  (let [pending (pending-hashtags state)]
    (cond
      (empty? pending)
      (assoc state :error nil)

      (within-hashtag-limit? state pending)
      (add-hashtags state pending)

      :else
      (assoc state :error :hashtag-limit))))

(defn remove-hashtag
  [state hashtag]
  (let [hashtag (normalize-hashtag hashtag)]
    (update state
            :hashtags
            (fn [hashtags]
              (vec (remove #{hashtag} hashtags))))))

(defn toggle-hashtag
  "A click on a recent hashtag selects or deselects it."
  [state hashtag]
  (if (contains? (set (:hashtags state))
                 (normalize-hashtag hashtag))
    (remove-hashtag state hashtag)
    (add-hashtags state [hashtag])))

(defn extract-username
  [username]
  (if (str/starts-with? (str username) "@")
    (subs username 1)
    (str username)))

(defn normalize-username
  [username]
  (some-> username
          (str/trim)
          (extract-username)
          (str/lower-case)))

(defn high-surrogate?
  "Whether the UTF-16 code unit at `idx` of `s` is the first half of a
   surrogate pair (an emoji, for example)."
  [s idx]
  (<= 0xD800
      #?(:clj (int (.charAt ^String s idx))
         :cljs (.charCodeAt s idx))
      0xDBFF))

(defn cap
  "At most the first `n` characters of `s`, without the first half of
   an emoji at the end: `subs` counts UTF-16 code units and cuts a
   surrogate pair in two, and FabricJS throws 'High surrogate without
   following low surrogate' when it renders the sticker of such a text."
  [s n]
  (let [end (min (count s) n)]
    (if (and (pos? end)
             (high-surrogate? s (dec end)))
      (subs s 0 (dec end))
      (subs s 0 end))))

(def max-question-length
  "Instagram allows a varying amount of characters between 30 and >60
   depending on the width of each character; 49 works in most cases."
  49)

(defn tidy-question
  "Upper case, single line, at most `max-question-length` characters:
   the question of poll and quiz stickers."
  [s]
  (let [s (str/replace (str/upper-case (str s))
                       #"\r?\n" "")]
    (cap s max-question-length)))

(def max-option-length
  53)

(defn tidy-option
  "The text of a poll tally or a quiz option, or nil when too long (the
   input keeps its previous value then)."
  [s]
  (let [s (str/triml (str s))]
    (when (<= (count s) max-option-length)
      s)))

(defn pad-to
  [n coll]
  (vec (concat coll
               (repeat (- n (count coll)) nil))))

(defn poll-tallies
  "The four tally slots of the poll dialog, seeded from an existing poll
   sticker (`:tallies`, or the `:answer-1` / `:answer-2` of the old poll
   format) or the defaults."
  [data]
  (pad-to 4
          (cond
            (:tallies data) (:tallies data)
            (:answer-1 data) [{:text (:answer-1 data) :font_size 42}
                              {:text (:answer-2 data) :font_size 42}]
            :else [{:text "Yes" :font_size 42}
                   {:text "No" :font_size 42}])))

(defn set-tally-text
  [state idx text]
  (if-let [text (tidy-option text)]
    (assoc-in state [:tallies idx] {:text text
                                    :font_size 42})
    state))

(defn valid-poll
  "The poll sticker data, or nil while fewer than two tallies have a
   text."
  [{:keys [tallies question color]}]
  (let [tallies (vec (remove (fn [tally]
                               (str/blank? (:text tally)))
                             tallies))]
    (when (<= 2 (count tallies))
      {:color color
       :question (tidy-question question)
       :tallies tallies})))

(defn quiz-options
  [data]
  (pad-to 4
          (or (:options data)
              ["Option 1" "Option 2"])))

(defn set-quiz-option
  [state idx text]
  (if-let [text (tidy-option text)]
    (assoc-in state [:options idx] text)
    state))

(defn valid-quiz
  "The quiz sticker data, or nil: the options must be filled from the
   top (at least two) and the correct answer must be one of them."
  [{:keys [options correct_answer] :as state}]
  (let [blanks (mapv (comp boolean str/blank?) options)]
    (when (and (contains? #{[false false true true]
                            [false false false true]
                            [false false false false]}
                          blanks)
               (not (get blanks correct_answer)))
      (merge (select-keys state [:text_color
                                 :start_background_color
                                 :end_background_color])
             {:question (tidy-question (:question state))
              :options (vec (remove str/blank? options))
              :correct_answer correct_answer}))))

(defn countdown-text
  "Upper case, at most two lines: the name of a countdown sticker."
  [s]
  (->> (str/split-lines (str/triml (str/upper-case (str s))))
       (take 2)
       (str/join "\n")))

(def max-question-sticker-length
  69)

(defn question-text
  [s]
  (cap (str s) max-question-sticker-length))

(defn text-color-for
  "White text on colored backgrounds, black on white (question and
   emoji slider stickers)."
  [background-color]
  (if (= background-color "#ffffff")
    "#000000"
    "#ffffff"))

(defn remove-https
  "The domain of `link`, the default text of a link sticker."
  [link]
  (let [complete-link (cond
                        (str/starts-with? link "https://www.") (subs link 12)
                        (str/starts-with? link "https://") (subs link 8)
                        (str/starts-with? link "http://www.") (subs link 11)
                        (str/starts-with? link "http://") (subs link 7)
                        :else link)]
    (first (str/split complete-link #"/"))))

(defn link-state
  [{:keys [link]}]
  {:valid? (boolean (link/validate-weblink? link))
   :too-long? (boolean (link/too-long? link))})

(defn link-text
  [{:keys [link sticker-name]}]
  (if (str/blank? sticker-name)
    (remove-https link)
    sticker-name))

(defn insert-at
  "Inserts `insertion` into `text` between the selection `start` and
   `end` (the cursor of the field), e.g. an emoji."
  [text start end insertion]
  (let [text (str text)
        start (min (or start (count text)) (count text))
        end (min (max (or end start) start) (count text))]
    (str (subs text 0 start)
         insertion
         (subs text end))))

(defn set-field
  "Applies a typed `value` to the `field` of the dialog state, with the
   rules of the dialog `:kind`: the poll and quiz questions are upper
   case and capped, their tallies / options refuse texts that are too
   long, the countdown name is upper case with two lines, the question
   sticker text is capped, a typed hashtag loses its newlines and a
   username its leading '@'. `field` is a keyword or a path like
   `[:tallies 1]`."
  [{:keys [kind] :as state} field value]
  (let [value (str value)]
    (cond
      (and (vector? field) (= :tallies (first field)))
      (set-tally-text state (second field) value)

      (and (vector? field) (= :options (first field)))
      (set-quiz-option state (second field) value)

      (and (#{:poll :quiz} kind) (= :question field))
      (assoc state :question (tidy-question value))

      (and (= :countdown kind) (= :text field))
      (assoc state :text (countdown-text value))

      (and (= :question kind) (= :question field))
      (assoc state :question (question-text value))

      (and (= :hashtag kind) (= :input field))
      (assoc state :input (str/replace value "\n" "") :error nil)

      (and (= :mention kind) (= :username field))
      (assoc state
             :username (extract-username value)
             :status :ready
             :result nil
             :error-message nil)

      :else
      (assoc state field value))))

(defn field-value
  [state field]
  (if (vector? field)
    (let [v (get-in state field)]
      (if (map? v) (:text v) v))
    (get state field)))
